import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { CheckCircle, XCircle, RefreshCw } from 'lucide-react';
import { api } from '../../utils/api';

const GoogleCalendarCallback = () => {
  const [status, setStatus] = useState('processing'); // 'processing', 'success', 'error'
  const [message, setMessage] = useState('Connecting to Google Calendar...');
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    handleCallback();
  }, []);

  const handleCallback = async () => {
    try {
      // Extract code and state from URL params
      const params = new URLSearchParams(location.search);
      const code = params.get('code');
      const state = params.get('state');
      const error = params.get('error');

      // Check if user denied access
      if (error) {
        setStatus('error');
        setMessage(error === 'access_denied' 
          ? 'You denied access to Google Calendar' 
          : `Error: ${error}`);
        
        // Notify parent window if opened in popup
        if (window.opener) {
          window.opener.postMessage({
            type: 'google-calendar-auth-error',
            error: error
          }, window.location.origin);
          
          setTimeout(() => window.close(), 2000);
        } else {
          setTimeout(() => navigate('/profile'), 3000);
        }
        return;
      }

      if (!code) {
        throw new Error('No authorization code received');
      }

      setMessage('Exchanging authorization code for tokens...');

      // Exchange code for tokens
      const response = await fetch(`${api.baseURL}/api/google-calendar/auth/callback`, {
        method: 'POST',
        headers: {
          ...api.getAuthHeaders(),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ code, state })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to connect Google Calendar');
      }

      const data = await response.json();

      setStatus('success');
      setMessage(`Successfully connected to ${data.email || 'Google Calendar'}!`);

      // Notify parent window if opened in popup
      if (window.opener) {
        window.opener.postMessage({
          type: 'google-calendar-auth-success',
          email: data.email
        }, window.location.origin);
        
        setTimeout(() => window.close(), 1500);
      } else {
        // If not in popup, redirect to profile after 2 seconds
        setTimeout(() => navigate('/profile'), 2000);
      }
    } catch (error) {
      console.error('OAuth callback error:', error);
      setStatus('error');
      setMessage(error.message || 'Failed to connect Google Calendar');

      // Notify parent window if opened in popup
      if (window.opener) {
        window.opener.postMessage({
          type: 'google-calendar-auth-error',
          error: error.message
        }, window.location.origin);
        
        setTimeout(() => window.close(), 3000);
      } else {
        setTimeout(() => navigate('/profile'), 3000);
      }
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="max-w-md w-full bg-white rounded-lg shadow-lg p-8">
        <div className="text-center">
          {status === 'processing' && (
            <>
              <RefreshCw className="h-16 w-16 text-blue-600 mx-auto mb-4 animate-spin" />
              <h2 className="text-2xl font-bold text-gray-900 mb-2">
                Connecting...
              </h2>
              <p className="text-gray-600">{message}</p>
            </>
          )}

          {status === 'success' && (
            <>
              <CheckCircle className="h-16 w-16 text-green-600 mx-auto mb-4" />
              <h2 className="text-2xl font-bold text-gray-900 mb-2">
                Success!
              </h2>
              <p className="text-gray-600">{message}</p>
              <p className="text-sm text-gray-500 mt-4">
                {window.opener ? 'Closing this window...' : 'Redirecting to your profile...'}
              </p>
            </>
          )}

          {status === 'error' && (
            <>
              <XCircle className="h-16 w-16 text-red-600 mx-auto mb-4" />
              <h2 className="text-2xl font-bold text-gray-900 mb-2">
                Connection Failed
              </h2>
              <p className="text-gray-600 mb-4">{message}</p>
              <p className="text-sm text-gray-500">
                {window.opener ? 'Closing this window...' : 'Redirecting to your profile...'}
              </p>
              {!window.opener && (
                <button
                  onClick={() => navigate('/profile')}
                  className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  Go to Profile
                </button>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default GoogleCalendarCallback;
